import React from 'react';
import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from "recharts";
import { getMaterialColorByRank } from "./collectionsEvolutionChartColors";
import { formatEvolutionAxisTick, formatPeriodLabel } from "./collectionsEvolutionChartFormatting";
import { getSegmentRadii, roundedBarPath } from "./collectionsEvolutionChartBarShape";
import { fmtWeightStr } from "@/utils/formatWeight";

interface EvolutionChartProps {
  data: Array<{
    period: string;
    [materialId: string]: number | string;
  }>;
  /** Nome de exibição por material_id — chave usada nos pontos de `data`. */
  namesById: Record<string, string>;
  /** Ordem de empilhamento (maior total primeiro = base da pilha) — mesma ordem da legenda. */
  materialOrder: string[];
  granularity: 'day' | 'week' | 'month';
  metric: 'units' | 'weight' | 'volume';
}

/** Cor de eixo do Figma (node 124:13041): black/40% — não é um tom de cinza fixo, é preto com opacidade. */
const AXIS_TICK_COLOR = 'rgba(0,0,0,0.4)';

/** Raio da ponta visível da pilha — Figma (node 124:13041) usa 8px. */
const BAR_STACK_RADIUS = 8;

/** Margem esquerda do gráfico e largura do eixo Y — o rótulo de unidade alinha por elas. */
const CHART_MARGIN_LEFT = 10;
// 64px: cabe o tick mais longo ("12,5 mil"). Com 56 o Recharts estimava que
// "300 mil" não cabia.
const Y_AXIS_WIDTH = 64;

export const EvolutionChart = ({ data, namesById, materialOrder, granularity, metric }: EvolutionChartProps) => {
  // Ensure data is valid and contains no NaN values
  const validData = data.map(item => {
    const cleanItem: { period: string; [materialId: string]: number | string } = { period: item.period };
    Object.keys(item).forEach(key => {
      if (key !== 'period') {
        const value = Number(item[key]);
        if (isNaN(value) || !isFinite(value) || value < 0) {
          cleanItem[key] = 0;
        } else {
          cleanItem[key] = Math.round(value * 100) / 100;
        }
      }
    });
    return cleanItem;
  }).filter(item => {
    if (!item.period) return false;
    const values = Object.keys(item).filter(key => key !== 'period').map(key => Number(item[key]));
    return values.some(value => !isNaN(value) && isFinite(value) && value > 0);
  });

  if (validData.length === 0) {
    return (
      <div className="h-full w-full flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <p className="text-sm">Nenhum dado disponível</p>
          <p className="text-xs text-muted-foreground/70">Ajuste os filtros para ver os resultados</p>
        </div>
      </div>
    );
  }

  const getUnit = () => {
    switch (metric) {
      case 'volume':
        return 'L';
      case 'units':
        return 'unidades';
      case 'weight':
      default:
        return 'kg';
    }
  };

  // Get angle and height based on granularity for better label display
  const getXAxisConfig = () => {
    switch (granularity) {
      case 'day':
        return { angle: -45, height: 50 };
      case 'week':
        return { angle: -30, height: 45 };
      case 'month':
        return { angle: -30, height: 40 };
      default:
        return { angle: -45, height: 50 };
    }
  };

  const xAxisConfig = getXAxisConfig();

  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  // Segue a ordem já calculada pelo pai (maior total primeiro = base da
  // pilha), só filtrando pra quem de fato tem valor em algum período válido.
  const materialIdsWithData = materialOrder.filter(materialId =>
    validData.some(item => {
      const value = Number(item[materialId]);
      return !isNaN(value) && isFinite(value) && value > 0;
    })
  );

  // `materialIdsWithData` está em ordem de pilha (menor primeiro) — a cor é
  // por rank na ordem da LEGENDA, que é a inversa.
  const rankByMaterialId = new Map(
    [...materialIdsWithData].reverse().map((materialId, rank) => [materialId, rank]),
  );

  return (
    <div className="relative h-full w-full">
      {/* Unidade uma vez sobre o eixo, não em cada tick: "100.000 kg" não
          cabe no eixo e quebrava em duas linhas. */}
      <span
        className="pointer-events-none absolute top-0 pr-2 text-right text-xs"
        style={{ left: 0, width: CHART_MARGIN_LEFT + Y_AXIS_WIDTH, color: AXIS_TICK_COLOR }}
      >
        {getUnit()}
      </span>
      <ChartContainer config={{}} className="h-full w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={validData}
            barCategoryGap="20%"
            maxBarSize={28}
            margin={{
              top: 24,
              right: 10,
              left: CHART_MARGIN_LEFT,
              bottom: xAxisConfig.height
            }}
          >
            <XAxis
              dataKey="period"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: AXIS_TICK_COLOR }}
              angle={xAxisConfig.angle}
              textAnchor="end"
              height={xAxisConfig.height}
              tickFormatter={(value) => formatPeriodLabel(String(value), granularity)}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: AXIS_TICK_COLOR }}
              width={Y_AXIS_WIDTH}
              // "mil"/"mi" por extenso nas três métricas — "K" gerava dúvida
              // de leitura. Unidade fica no rótulo acima do eixo.
              tickFormatter={(value) => formatEvolutionAxisTick(Number(value))}
            />

            {materialIdsWithData.map((materialId) => (
              <Bar
                key={materialId}
                dataKey={materialId}
                stackId="materials"
                fill={getMaterialColorByRank(rankByMaterialId.get(materialId) ?? 0)}
                isAnimationActive={!prefersReducedMotion}
                animationDuration={550}
                animationEasing="ease-out"
                shape={(props: {
                  x?: number;
                  y?: number;
                  width?: number;
                  height?: number;
                  fill?: string;
                  payload?: Record<string, number | string>;
                }) => {
                  const { x = 0, y = 0, width = 0, height = 0, fill, payload } = props;
                  // Só a ponta visível da pilha arredonda (topo do segmento
                  // mais alto, base do mais baixo — ignorando resíduos de
                  // arredondamento invisíveis, ver getSegmentRadii) — não o
                  // índice fixo do material, que erra sempre que ele zera
                  // num período.
                  const { topRadius, bottomRadius } = getSegmentRadii(
                    materialIdsWithData,
                    payload,
                    materialId,
                    BAR_STACK_RADIUS,
                  );
                  return (
                    <path d={roundedBarPath(x, y, width, height, topRadius, bottomRadius)} fill={fill} />
                  );
                }}
              />
            ))}

            <ChartTooltip
              cursor={false}
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-white p-3 border-0 rounded-lg shadow-lg max-w-xs">
                      <p className="font-semibold mb-2 text-sm">
                        {formatPeriodLabel(String(label), granularity)}
                      </p>
                      {/* Maior primeiro, na mesma leitura de cima pra baixo
                          da pilha — `payload` vem na ordem de declaração dos
                          <Bar>, que é a inversa (base primeiro). */}
                      {[...payload]
                        .sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0))
                        .map((entry, index) => {
                        const value = Number(entry.value);
                        if (isNaN(value) || !isFinite(value) || value <= 0) return null;

                        const unit = getUnit();
                        const materialId = String(entry.dataKey);
                        const name = namesById[materialId] ?? 'Outros';

                        return (
                          <div key={index} className="flex items-center space-x-2 mb-1">
                            <div
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: entry.color }}
                            />
                            {/* Número cheio, sem abreviar: é aqui que o cliente
                                confere o valor contra a planilha/fatura, e
                                "4,3K" somado por material não fechava com o
                                total (o truncamento comia até 99 kg por linha). */}
                            <span className="text-xs">
                              {name}:{' '}
                              {metric === 'weight'
                                ? fmtWeightStr(value)
                                : `${Math.trunc(value).toLocaleString('pt-BR')} ${unit}`}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                }
                return null;
              }}
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartContainer>
    </div>
  );
};
